import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Trophy, Users, Brain, ChevronRight, Zap, Target, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  const features = [
    {
      icon: Brain,
      title: 'AI Coach',
      description: 'Stockfish 18 engine with real-time move analysis',
      color: 'blue'
    },
    {
      icon: Trophy,
      title: 'ELO Rankings',
      description: 'Climb the leaderboard and track your progress',
      color: 'orange'
    },
    {
      icon: Users,
      title: 'Multiplayer',
      description: 'Challenge friends with real-time WebSocket duels',
      color: 'purple'
    },
    {
      icon: Zap,
      title: 'Instant Play',
      description: 'No downloads. Play in your browser instantly',
      color: 'blue'
    }
  ];

  const games = [
    {
      name: 'Chess',
      status: 'live',
      emoji: '♟️',
      description: 'Classic strategy with Stockfish AI',
      path: '/games/chess',
      players: '2.4k',
      accent: 'blue'
    },
    {
      name: 'Checkers',
      status: 'live',
      emoji: '⚫',
      description: 'American & International rules',
      path: '/games/checkers',
      players: '1.2k',
      accent: 'orange'
    },
    {
      name: 'Triangle',
      status: 'live',
      emoji: '🔺',
      description: 'Hexagonal racing game',
      path: '/games/triangle-checkers',
      players: '890',
      accent: 'purple'
    },
    {
      name: 'Baseball',
      status: 'live',
      emoji: '⚾',
      description: 'Home Run Derby & PvP',
      path: '/games/baseball',
      players: '650',
      accent: 'amber'
    },
    {
      name: 'Bridge',
      status: 'live',
      emoji: '🃏',
      description: 'Trick-taking card game',
      path: '/games/bridge',
      players: '420',
      accent: 'rose'
    },
    {
      name: 'Poker',
      status: 'live',
      emoji: '🎰',
      description: 'Texas Hold\'em & Three Card',
      path: '/games/poker/texas-holdem',
      players: '380',
      accent: 'emerald'
    },
    {
      name: 'Teen Patti',
      status: 'live',
      emoji: '🃏',
      description: '13 Dealer\'s Choice variations',
      path: '/games/teen-patti',
      players: '520',
      accent: 'amber',
      isNew: true
    },
    {
      name: 'Solitaire',
      status: 'live',
      emoji: '🂡',
      description: 'Classic Klondike card game',
      path: '/games/solitaire',
      players: '750',
      accent: 'emerald'
    }
  ];

  const accentColors = {
    blue: { border: 'border-blue-500/30', bg: 'bg-blue-500/10', text: 'text-blue-400', glow: 'hover:shadow-blue-500/20' },
    orange: { border: 'border-orange-500/30', bg: 'bg-orange-500/10', text: 'text-orange-400', glow: 'hover:shadow-orange-500/20' },
    purple: { border: 'border-purple-500/30', bg: 'bg-purple-500/10', text: 'text-purple-400', glow: 'hover:shadow-purple-500/20' },
    rose: { border: 'border-rose-500/30', bg: 'bg-rose-500/10', text: 'text-rose-400', glow: 'hover:shadow-rose-500/20' },
    amber: { border: 'border-amber-500/30', bg: 'bg-amber-500/10', text: 'text-amber-400', glow: 'hover:shadow-amber-500/20' },
    emerald: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-400', glow: 'hover:shadow-emerald-500/20' }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0a1a] via-[#1a1025] to-[#0f0a1a]">
      {/* Cosmic background with stars */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-purple-900/20 via-transparent to-transparent" />
        <div className="absolute top-20 left-10 w-2 h-2 bg-white rounded-full animate-pulse opacity-60" />
        <div className="absolute top-40 right-20 w-1 h-1 bg-blue-300 rounded-full animate-pulse opacity-40" />
        <div className="absolute top-60 left-1/4 w-1.5 h-1.5 bg-orange-300 rounded-full animate-pulse opacity-50" />
        <div className="absolute bottom-40 right-1/3 w-1 h-1 bg-purple-300 rounded-full animate-pulse opacity-40" />
        <div className="absolute bottom-60 left-1/3 w-2 h-2 bg-blue-400 rounded-full animate-pulse opacity-30" />
      </div>
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0f0a1a]/90 backdrop-blur-md border-b border-purple-900/30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_4e3de3c9-461d-4f73-ada0-30b07137d950/artifacts/0wd7buyq_Boosty%20Games%20v2.png" 
              alt="Boosty Games" 
              className="h-12 w-auto"
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-slate-300 hover:text-white hover:bg-purple-900/30"
              data-testid="nav-browse-btn"
            >
              Browse Games
            </Button>
            {isAuthenticated ? (
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold shadow-lg shadow-orange-500/25"
                data-testid="nav-dashboard-btn"
              >
                Dashboard
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/auth?mode=login')}
                  className="text-slate-300 hover:text-white hover:bg-purple-900/30"
                  data-testid="nav-login-btn"
                >
                  Log In
                </Button>
                <Button
                  onClick={() => navigate('/auth?mode=signup')}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold shadow-lg shadow-orange-500/25"
                  data-testid="nav-signup-btn"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section - Logo Centered */}
      <section className="pt-24 pb-8 px-4 relative">
        <div className="max-w-5xl mx-auto text-center">
          {/* Giant Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_4e3de3c9-461d-4f73-ada0-30b07137d950/artifacts/0wd7buyq_Boosty%20Games%20v2.png" 
              alt="Boosty Games" 
              className="h-40 sm:h-52 md:h-64 w-auto mx-auto drop-shadow-2xl"
              style={{ filter: 'drop-shadow(0 0 40px rgba(249, 115, 22, 0.3))' }}
            />
          </motion.div>

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <p className="text-xl sm:text-2xl text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Play <span className="text-blue-400 font-semibold">Chess</span>, 
              <span className="text-orange-400 font-semibold"> Checkers</span>, 
              <span className="text-purple-400 font-semibold"> Bridge</span>, 
              <span className="text-blue-400 font-semibold"> Baseball</span> and more with intelligent AI opponents
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center mb-12">
              <Button
                size="lg"
                onClick={handleGetStarted}
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-bold px-10 h-14 text-lg shadow-xl shadow-orange-500/30"
                data-testid="hero-get-started-btn"
              >
                Start Playing
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/games/chess')}
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400 h-14 text-lg px-8"
                data-testid="hero-guest-btn"
              >
                <Target className="w-5 h-5 mr-2" />
                Try as Guest
              </Button>
            </div>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex justify-center gap-8 sm:gap-16"
          >
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-black text-orange-400">5.6k+</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Active Players</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-black text-blue-400">8</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">Games Live</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-black text-purple-400">24/7</div>
              <div className="text-xs text-slate-500 uppercase tracking-wider">AI Available</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Games Grid */}
      <section className="py-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl font-bold text-white text-center mb-8">Choose Your Game</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {games.map((game, index) => {
              const colors = accentColors[game.accent];
              return (
                <motion.div
                  key={game.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  onClick={() => game.path && navigate(game.path)}
                  className={`
                    relative p-4 rounded-xl border ${colors.border} bg-[#1a1025]/80 backdrop-blur-sm
                    cursor-pointer transition-all duration-200 hover:scale-[1.03] 
                    hover:border-opacity-80 active:scale-[0.98]
                    touch-manipulation select-none
                  `}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                  data-testid={`game-card-${game.name.toLowerCase()}`}
                >
                  {(game.name === 'Poker' || game.isNew) && (
                    <span className="absolute -top-2 -right-2 px-2 py-0.5 text-[10px] font-bold bg-orange-500 text-white rounded-full">NEW</span>
                  )}
                  <div className="text-4xl mb-3 text-center">{game.emoji}</div>
                  <h3 className="text-sm font-semibold text-white text-center mb-1">{game.name}</h3>
                  <p className="text-xs text-slate-500 text-center line-clamp-2">{game.description}</p>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs text-slate-400 font-mono">{game.players}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 border-t border-purple-900/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-3">
              Built for Competitive Play
            </h2>
            <p className="text-slate-400 max-w-lg mx-auto">
              Everything you need to improve your game and track your progress
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((feature, index) => {
              const colors = accentColors[feature.color];
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.1 }}
                  className={`
                    p-5 rounded-xl border ${colors.border} bg-[#1a1025]/80 backdrop-blur-sm
                    hover:border-opacity-60 transition-colors
                  `}
                >
                  <div className={`
                    w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center mb-4
                  `}>
                    <feature.icon className={`w-5 h-5 ${colors.text}`} />
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1">{feature.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="p-8 rounded-2xl border border-orange-500/30 bg-[#1a1025]/80 text-center relative overflow-hidden">
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-600/10 via-transparent to-purple-600/10 pointer-events-none" />
            <Crown className="w-10 h-10 text-orange-400 mx-auto mb-4 relative z-10" />
            <h2 className="font-display text-2xl font-bold text-white mb-3 relative z-10">
              Ready to Compete?
            </h2>
            <p className="text-slate-400 mb-6 text-sm relative z-10">
              Create a free account to save your progress and climb the leaderboard
            </p>
            <Button
              size="lg"
              onClick={handleGetStarted}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-bold px-8 shadow-lg shadow-orange-500/25 relative z-10"
              data-testid="cta-signup-btn"
            >
              Create Free Account
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-purple-900/30">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_4e3de3c9-461d-4f73-ada0-30b07137d950/artifacts/0wd7buyq_Boosty%20Games%20v2.png" 
              alt="Boosty Games" 
              className="h-10 w-auto"
            />
            <span className="text-slate-500 text-sm">© 2026 Boosty Games</span>
          </div>
          <div className="flex gap-6 text-xs text-slate-600">
            <a href="#" className="hover:text-orange-400 transition-colors">Privacy</a>
            <a href="#" className="hover:text-orange-400 transition-colors">Terms</a>
            <a href="#" className="hover:text-orange-400 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
